import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { loadAllAndAssert, loadAndAssert, resolveOwnerID } from '../authz/access.js';
import { Exam } from '../exam.js';
import { Submission } from '../submission.js';
import { newSubmissionDocument } from './create-submission.js';

/** One student row of the review canvas: an existing submission (`submissionId`) or a new one (`tempId`). */
export interface SubmissionAssignmentEntry {
  submissionId?: string;
  tempId?: string;
  /** Empty leaves the stored name as it is. */
  studentName: string;
  imageIds: string[];
}

export interface SubmissionAssignment {
  submissions: SubmissionAssignmentEntry[];
  deleteSubmissionIds: string[];
}

/** Stable marker the client matches on: the assignment no longer fits the stored submissions and must be reloaded. */
export const SUBMISSION_ASSIGNMENT_INVALID = 'SUBMISSION_ASSIGNMENT_INVALID';

const invalid = (reason: string) =>
  new ExpectedError(`${SUBMISSION_ASSIGNMENT_INVALID}: ${reason}`, {
    translationKey: 'submissions.assignment_invalid',
  });

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string');

function parseAssignment(assignmentJson: string): SubmissionAssignment {
  let parsed: any;
  try {
    parsed = JSON.parse(assignmentJson);
  } catch {
    throw invalid('not JSON');
  }
  const entries = Array.isArray(parsed?.submissions) ? parsed.submissions : null;
  if (!entries || !isStringList(parsed?.deleteSubmissionIds)) throw invalid('malformed');
  for (const entry of entries) {
    const hasOneId = (typeof entry?.submissionId === 'string') !== (typeof entry?.tempId === 'string');
    if (!hasOneId || typeof entry.studentName !== 'string' || !isStringList(entry.imageIds)) throw invalid('malformed');
  }
  return { submissions: entries, deleteSubmissionIds: parsed.deleteSubmissionIds };
}

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * Saves the whole page-to-student assignment of the review canvas in one call: creates the new students, moves the
 * pages, renames, and deletes the removed students — in that order, so the delete cascade never takes the files of
 * pages that moved elsewhere (it keeps files still referenced, see delete-cascade.ts). The canvas saves on every change.
 *
 * Checked before anything is written:
 *  - every page of the mentioned (and deleted) submissions is assigned exactly once, and no other page appears. The
 *    pages therefore all come from the caller's own exam: nobody else's scan can be attached this way.
 *  - confirmed submissions (`splitConfirmed`) are locked: their pages stay, they are not deleted, their name stays.
 * Submissions the canvas does not know yet (e.g. a split finishing meanwhile) are not mentioned and stay untouched.
 *
 * Not one transaction: a split confirmed in the milliseconds between the check and the write is caught by the
 * `splitConfirmed` guard on each update and reported, but the rest of that save is already written.
 *
 * Returns the ids of the created submissions, in the order of the new entries.
 */
export const saveSubmissionAssignmentMutation = registerMutation({
  operationName: 'saveSubmissionAssignmentMutation',
  type: () => [String],
  params: [
    { name: 'examId', type: () => String },
    { name: 'assignmentJson', type: () => String },
  ],
  resolve: (examId: string, assignmentJson: string) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const assignment = parseAssignment(assignmentJson);

    const submissionCollection = await getRawCollection(Submission._collectionName);
    const stored = await submissionCollection
      .find({ examId }, { projection: { _id: 1, imageIds: 1, studentName: 1, splitConfirmed: 1 } })
      .toArray();
    const byId = new Map(stored.map(submission => [String(submission._id), submission]));
    const pagesOf = (submissionId: string) => {
      const imageIds = byId.get(submissionId)?.imageIds;
      return Array.isArray(imageIds) ? imageIds.map(String) : [];
    };

    const existing = assignment.submissions.filter(entry => entry.submissionId !== undefined);
    const created = assignment.submissions.filter(entry => entry.tempId !== undefined);
    // Already gone (deleted by an earlier save): nothing left to do for them.
    const deleteIds = [...new Set(assignment.deleteSubmissionIds)].filter(id => byId.has(id));

    const mentioned = [...existing.map(entry => entry.submissionId!), ...deleteIds];
    if (new Set(mentioned).size !== mentioned.length) throw invalid('a submission is listed twice');
    if (mentioned.some(id => !byId.has(id))) throw invalid('a submission is not part of this exam');
    await loadAllAndAssert(
      Submission,
      existing.map(entry => entry.submissionId!),
      'update',
      { _id: 1 },
    );
    await loadAllAndAssert(Submission, deleteIds, 'delete', { _id: 1 });

    const pagesBefore = new Set(mentioned.flatMap(pagesOf));
    const pagesAfter = assignment.submissions.flatMap(entry => entry.imageIds);
    if (new Set(pagesAfter).size !== pagesAfter.length) throw invalid('a page is assigned twice');
    if (pagesAfter.length !== pagesBefore.size || pagesAfter.some(id => !pagesBefore.has(id))) {
      throw invalid('pages are missing or unknown');
    }

    if (deleteIds.some(id => byId.get(id)?.splitConfirmed)) throw invalid('a confirmed submission cannot be deleted');
    for (const entry of existing) {
      if (byId.get(entry.submissionId!)?.splitConfirmed && !sameList(pagesOf(entry.submissionId!), entry.imageIds)) {
        throw invalid('a confirmed submission cannot change');
      }
    }

    // Only what changed is written, so an unchanged save causes no updates (and no live-subscription events).
    const userID = await resolveOwnerID();
    const newDocuments = created.map(entry =>
      newSubmissionDocument({ examId, imageIds: entry.imageIds, studentName: entry.studentName.trim(), userID }),
    );
    const updates = existing.flatMap(entry => {
      const submissionId = entry.submissionId!;
      const current = byId.get(submissionId);
      if (current?.splitConfirmed) return [];
      const studentName = entry.studentName.trim();
      const set: { imageIds?: string[]; studentName?: string } = {};
      if (!sameList(pagesOf(submissionId), entry.imageIds)) set.imageIds = entry.imageIds;
      if (studentName && studentName !== current?.studentName) set.studentName = studentName;
      return Object.keys(set).length ? [{ submissionId, set }] : [];
    });

    if (newDocuments.length || updates.length) {
      const result = await submissionCollection.bulkWrite([
        ...newDocuments.map(({ data }) => ({ insertOne: { document: data } })),
        ...updates.map(({ submissionId, set }) => ({
          updateOne: { filter: { _id: submissionId as any, splitConfirmed: { $ne: true } }, update: { $set: set } },
        })),
      ]);
      if (result.matchedCount !== updates.length) {
        logger.warn(
          { examId, context: 'saveSubmissionAssignment' },
          'Submission confirmed while its assignment was saved',
        );
        throw invalid('a submission was confirmed meanwhile');
      }
    }

    // Like the manual add before it: every created submission counts into `submissionsFound`, which the exam's
    // job conditions read (job-conditions.ts `hasSubmissions`, see updateSubmissionsFoundMutation).
    if (newDocuments.length) {
      const exams = await getRawCollection(Exam._collectionName);
      const counter = 'examState.uploadProgress.submissionsFound';
      const incremented = await exams.updateOne(
        { _id: examId as any, [counter]: { $type: 'number' } },
        { $inc: { [counter]: newDocuments.length } },
      );
      if (!incremented.matchedCount) {
        await exams.updateOne({ _id: examId as any }, { $set: { [counter]: newDocuments.length } });
      }
    }

    // Imported here: this module also reaches the client bundle (chat.tsx), the cascade is server only.
    if (deleteIds.length) {
      const { deleteSubmissionCascade } = await import('../utils/delete-cascade.js');
      for (const submissionId of deleteIds) await deleteSubmissionCascade(submissionId);
    }

    logger.info(
      {
        examId,
        created: newDocuments.length,
        updated: updates.length,
        deleted: deleteIds.length,
        context: 'saveSubmissionAssignment',
      },
      'Submission assignment saved',
    );
    return newDocuments.map(({ submission }) => submission._id);
  },
});
